import type { Suggestion } from '../../../components/UI/SearchWithAutocomplete'
import {
  SUBGENRE_OPTIONS as REGISTRY_SUBGENRE_OPTIONS,
  SUBGENRE_REGISTRIES,
  getGroupedSubgenres,
} from '../../../data/subgenreRegistry'

// ─── Helper to convert string[] to Suggestion[] ───
const toSuggestions = (items: string[], type?: string): Suggestion[] =>
  items.map((item, i) => ({ id: `${type || 'tag'}-${i}`, label: item, type }))

// ═══════════════════════════════════════════════════
// ROMANCE
// ═══════════════════════════════════════════════════
export const ROMANCE_MODULE_DATA = {
  pairingTypes: [
    'MF',
    'MM',
    'FF',
    'MMF',
    'MFM',
    'Reverse Harem / Why Choose',
    'Polyamorous',
  ] as const,

  tropes: [
    'Enemies to Lovers',
    'Friends to Lovers',
    'Forbidden Love',
    'Fake Dating',
    'Second Chance',
    'Marriage of Convenience',
    'Forced Proximity',
    'Slow Burn',
    'Grumpy/Sunshine',
    'Only One Bed',
    'Age Gap',
    'Boss/Employee',
    "Brother's Best Friend",
    'Bodyguard',
    'Captor/Captive',
    'Arranged Marriage',
    'Secret Identity',
    'Childhood Sweethearts',
    'Opposites Attract',
    'Love Triangle',
    'Billionaire',
    'Rockstar',
    'Sports Romance',
    'Military',
    'Single Parent',
    'Nanny/Babysitter',
    'Teacher/Student',
    'Mafia',
    'Bully Romance',
    'Stalker Romance',
    'Obsessive Love',
    'Possessive Hero',
  ],

  characterPairings: [
    'Alpha Hero',
    'Beta Hero',
    'Omega Hero',
    'Morally Grey Hero',
    'Villain Hero',
    'Cinnamon Roll Hero',
    'Strong Heroine',
    'Shy Heroine',
    'Kickass Heroine',
    'Damaged Hero',
    'Reformed Rake',
    'Ice Queen',
    'Sunshine Heroine',
    'Brooding Hero',
    'Protective Hero',
    'Reluctant Hero',
  ],

  endingTypes: [
    'HEA',
    'HFN',
    'Cliffhanger',
    'Bittersweet',
    'Tragic',
    'Open Ending',
  ] as const,

  spiceDescriptions: {
    1: {
      label: 'Mild',
      description:
        'Closed door or fade-to-black — romance is implied, not shown',
      emoji: '🌶️',
    },
    2: {
      label: 'Warm',
      description: 'Light steam — some sensual scenes but not explicit',
      emoji: '🌶️',
    },
    3: {
      label: 'Spicy',
      description: 'Moderate heat — explicit scenes that are part of the story',
      emoji: '🌶️',
    },
    4: {
      label: 'Extra Hot',
      description: 'High heat — frequent, detailed intimate scenes',
      emoji: '🌶️',
    },
    5: {
      label: 'Inferno',
      description: 'Maximum heat — erotica-level content throughout',
      emoji: '🌶️',
    },
  } as Record<number, { label: string; description: string; emoji: string }>,

  warnings: [
    'Dubious Consent',
    'Non-Consent',
    'Power Imbalance',
    'Age Gap (Significant)',
    'Captivity',
    'Stalking',
    'Manipulation',
    'Violence Against Partner',
    'Breeding Kink',
    'Somnophilia',
    'Degradation',
    'Blood Play',
    'Knife Play',
    'Breath Play',
    'Ownership Dynamics',
    'Trafficking Themes',
  ],
}

export const ROMANCE_SUGGESTIONS = {
  tropes: toSuggestions(ROMANCE_MODULE_DATA.tropes, 'Romance Trope'),
  characterPairings: toSuggestions(
    ROMANCE_MODULE_DATA.characterPairings,
    'Character Pairing',
  ),
  warnings: toSuggestions(ROMANCE_MODULE_DATA.warnings, 'Romance Warning'),
}

// ═══════════════════════════════════════════════════
// FANTASY
// ═══════════════════════════════════════════════════
export const FANTASY_MODULE_DATA = {
  subtypes: [
    'High Fantasy',
    'Low Fantasy',
    'Dark Fantasy',
    'Urban Fantasy',
    'Sword and Sorcery',
    'Mythic Fantasy',
    'Cozy Fantasy',
  ] as const,

  magicSystems: [
    'Hard Magic',
    'Soft Magic',
    'Elemental',
    'Divine',
    'Forbidden Magic',
    'Necromancy',
    'Alchemy',
  ] as const,

  worldStructures: [
    'Secondary World',
    'Alternate Earth',
    'Portal Fantasy',
    'Hidden Magical World',
  ] as const,

  storyEngines: [
    'Quest',
    'Chosen One',
    'Found Family',
    'Political Intrigue',
    'Rebellion',
    'Court Politics',
    'Survival Journey',
  ],

  creatureTags: [
    'Fae',
    'Dragon',
    'Vampire',
    'Witch',
    'Demon',
    'Angel',
    'Monster',
    'Werewolf',
    'Shapeshifter',
    'Elf',
    'Dwarf',
    'Orc',
    'Undead',
    'Phoenix',
    'Griffin',
    'Mermaid',
    'Centaur',
    'Troll',
  ],

  tones: [
    { level: 'Light', description: 'Whimsical, cozy, or lighthearted fantasy' },
    {
      level: 'Medium',
      description: 'Balanced tone — adventure with some tension',
    },
    {
      level: 'Dark',
      description: 'Heavy themes — war, betrayal, moral ambiguity',
    },
    {
      level: 'Brutal / Grim',
      description: 'Grimdark — no safety, high stakes, graphic content',
    },
  ] as const,
}

export const FANTASY_SUGGESTIONS = {
  storyEngines: toSuggestions(
    FANTASY_MODULE_DATA.storyEngines,
    'Fantasy Engine',
  ),
  creatureTags: toSuggestions(FANTASY_MODULE_DATA.creatureTags, 'Creature'),
}

// ═══════════════════════════════════════════════════
// THRILLER / MYSTERY
// ═══════════════════════════════════════════════════
export const THRILLER_MYSTERY_MODULE_DATA = {
  subtypes: [
    'Psychological Thriller',
    'Crime Thriller',
    'Domestic Thriller',
    'Legal Thriller',
    'Political Thriller',
    'Mystery',
    'Cozy Mystery',
    'Noir',
  ] as const,

  investigationTypes: [
    'Detective',
    'Amateur Sleuth',
    'Journalist',
    'Law Enforcement',
    'Civilian Witness',
  ] as const,

  threatTypes: [
    'Serial Killer',
    'Missing Person',
    'Conspiracy',
    'Stalker',
    'Secret Society',
    'False Accusation',
    'Corporate Corruption',
    'Government Cover-Up',
    'Organized Crime',
  ],

  storyEngines: [
    'Whodunnit',
    'Cat and Mouse',
    'Locked Room',
    'Unreliable Narrator',
    'Twist-Driven',
    'Cold Case',
    'Survival Chase',
    'Heist',
  ],

  suspenseIntensities: [
    {
      level: 'Low',
      description: 'Cozy or light mystery — low stakes, gentle tension',
    },
    {
      level: 'Medium',
      description: 'Moderate suspense — engaging but not relentless',
    },
    {
      level: 'High',
      description: 'High tension — page-turner pacing, real danger',
    },
    {
      level: 'Extreme',
      description: 'Relentless intensity — psychological pressure throughout',
    },
  ] as const,
}

export const THRILLER_MYSTERY_SUGGESTIONS = {
  threatTypes: toSuggestions(
    THRILLER_MYSTERY_MODULE_DATA.threatTypes,
    'Threat',
  ),
  storyEngines: toSuggestions(
    THRILLER_MYSTERY_MODULE_DATA.storyEngines,
    'Story Engine',
  ),
}

// ═══════════════════════════════════════════════════
// HORROR
// ═══════════════════════════════════════════════════
export const HORROR_MODULE_DATA = {
  subtypes: [
    'Supernatural Horror',
    'Psychological Horror',
    'Gothic Horror',
    'Slasher',
    'Body Horror',
    'Cosmic Horror',
    'Folk Horror',
    'Survival Horror',
  ] as const,

  fearMechanisms: [
    'Possession',
    'Haunting',
    'Isolation',
    'Transformation',
    'Cults',
    'Monster Threat',
    'Loss of Sanity',
    'Home Invasion',
    'Paranoia',
    'Infection',
    'Doppelgänger',
    'Cursed Object',
  ],

  storyEngines: [
    'Descent into Madness',
    'Final Girl',
    'Containment Failure',
    'Haunted Place',
    'Ancient Evil Awakens',
    'Unstoppable Entity',
    'Ritual Gone Wrong',
    'Survival Night',
  ],

  intensities: [
    {
      level: 'Creeping',
      description: 'Atmospheric dread — slow build, psychological unease',
    },
    {
      level: 'Disturbing',
      description: 'Unsettling content — lingers after reading',
    },
    {
      level: 'Brutal',
      description: 'Graphic violence or body horror — visceral impact',
    },
    {
      level: 'Extreme',
      description: 'No limits — reader discretion strongly advised',
    },
  ] as const,

  extremeHorrorLayers: [
    'Splatter / Gore',
    'Taboo Horror',
    'Graphic Psychological Distress',
  ] as const,
}

export const HORROR_SUGGESTIONS = {
  fearMechanisms: toSuggestions(HORROR_MODULE_DATA.fearMechanisms, 'Fear'),
  storyEngines: toSuggestions(HORROR_MODULE_DATA.storyEngines, 'Horror Engine'),
}

// ═══════════════════════════════════════════════════
// SCI-FI
// ═══════════════════════════════════════════════════
export const SCIFI_MODULE_DATA = {
  subtypes: [
    'Dystopian',
    'Space Opera',
    'Cyberpunk',
    'Military Sci-Fi',
    'Post-Apocalyptic',
    'Time Travel',
    'First Contact',
    'Climate Sci-Fi',
  ] as const,

  coreConcepts: [
    'AI',
    'Androids',
    'Genetic Engineering',
    'Alien Contact',
    'Time Loops',
    'Space Colonization',
    'Surveillance State',
    'Virtual Reality',
    'Nanotechnology',
    'Terraforming',
    'Faster-Than-Light',
    'Cryogenics',
  ],

  scales: [
    {
      level: 'Personal',
      description: "One person's journey through a speculative world",
    },
    { level: 'City', description: 'City-scale conflict or transformation' },
    { level: 'Planetary', description: 'Planet-wide stakes or exploration' },
    { level: 'Galactic', description: 'Multi-system, interstellar scope' },
  ] as const,

  scienceDepths: [
    {
      level: 'Soft Sci-Fi',
      description: 'Science as backdrop — focus on characters and society',
    },
    {
      level: 'Medium Speculative',
      description: 'Plausible extrapolation — some technical grounding',
    },
    {
      level: 'Hard Sci-Fi',
      description: 'Rigorous science — accuracy matters to the story',
    },
  ] as const,

  storyEngines: [
    'Rebellion',
    'Discovery',
    'Survival',
    'Ethical Experiment',
    'War',
    'Collapse',
    'Escape',
    'First Contact',
  ],
}

export const SCIFI_SUGGESTIONS = {
  coreConcepts: toSuggestions(SCIFI_MODULE_DATA.coreConcepts, 'Concept'),
  storyEngines: toSuggestions(SCIFI_MODULE_DATA.storyEngines, 'Sci-Fi Engine'),
}

// ═══════════════════════════════════════════════════
// CONTEMPORARY / GENERAL FICTION
// ═══════════════════════════════════════════════════
export const CONTEMPORARY_MODULE_DATA = {
  subtypes: [
    "Women's Fiction",
    'Literary Fiction',
    'Family Drama',
    'Coming of Age',
    'Small Town Fiction',
    'Workplace Fiction',
    'Social Fiction',
  ] as const,

  conflictTypes: [
    'Internal',
    'Family',
    'Social',
    'Romantic',
    'Career',
    'Grief',
    'Identity',
    'Health',
    'Financial',
    'Cultural',
  ],

  storyEngines: [
    'Reinvention',
    'Returning Home',
    'Personal Growth',
    'Family Secrets',
    'Midlife Reset',
    'Friendship Fracture',
    'Healing Journey',
    'Coming Out',
    'Career Pivot',
    'Generational Saga',
  ],

  emotionalWeights: [
    { level: 'Light', description: 'Feel-good, uplifting, easy read' },
    { level: 'Warm', description: 'Heartfelt with some emotional depth' },
    {
      level: 'Emotional',
      description: 'Deeply moving — expect tears and catharsis',
    },
    {
      level: 'Heavy',
      description: 'Intense emotional weight — grief, trauma, transformation',
    },
  ] as const,
}

export const CONTEMPORARY_SUGGESTIONS = {
  conflictTypes: toSuggestions(
    CONTEMPORARY_MODULE_DATA.conflictTypes,
    'Conflict',
  ),
  storyEngines: toSuggestions(
    CONTEMPORARY_MODULE_DATA.storyEngines,
    'Story Engine',
  ),
}

// ═══════════════════════════════════════════════════
// GENRE → MODULE MAPPING
// ═══════════════════════════════════════════════════
export type GenreModuleKey =
  | 'romance'
  | 'fantasy'
  | 'thriller-mystery'
  | 'horror'
  | 'scifi'
  | 'contemporary'
  | 'fallback'

export const genreToModuleKey = (genre: string): GenreModuleKey => {
  const g = genre.toLowerCase()
  if (g === 'romance') return 'romance'
  if (g === 'fantasy') return 'fantasy'
  if (g === 'thriller' || g === 'mystery') return 'thriller-mystery'
  if (g === 'horror') return 'horror'
  if (g === 'science fiction' || g === 'sci-fi') return 'scifi'
  if (
    g === 'contemporary fiction' ||
    g === 'literary fiction' ||
    g === 'general fiction'
  )
    return 'contemporary'
  return 'fallback'
}

/**
 * Sub-genre options, derived from data/subgenreRegistry.ts — the single source
 * of truth for the taxonomy. Do NOT hand-edit; change the registry instead.
 */
export const SUBGENRE_OPTIONS: Record<string, string[]> = REGISTRY_SUBGENRE_OPTIONS

/**
 * Autocomplete suggestions grouped into Core / Crossover / Microgenre so the
 * expanded taxonomy stays readable instead of rendering as one long flat list.
 */
export const SUBGENRE_SUGGESTIONS: Record<string, Suggestion[]> =
  Object.fromEntries(
    Object.keys(SUBGENRE_REGISTRIES).map((genre) => [
      genre,
      getGroupedSubgenres(genre).flatMap((group) =>
        group.entries.map((entry) => ({
          id: `subgenre-${entry.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
          label: entry.displayLabel,
          group: group.label,
        })),
      ),
    ]),
  )

// ═══════════════════════════════════════════════════
// DARKNESS LEVEL CARDS (matching SpiceDarkness.tsx)
// ═══════════════════════════════════════════════════
export const DARKNESS_LEVEL_CARDS = [
  {
    level: 1,
    name: 'Hint of Shadow',
    emoji: '🌑',
    description:
      'Low-level emotional tension or light trauma backstories. The book remains hopeful in tone and avoids graphic descriptions.',
    examples: ['grief', 'mild trauma', 'emotional abuse'],
    selectedBg:
      'bg-slate-300 text-slate-900 border-slate-400 dark:bg-slate-600 dark:text-white dark:border-slate-500',
  },
  {
    level: 2,
    name: 'Into the Grey',
    emoji: '🌑',
    description:
      'Moderate emotional weight or trauma explored through flashbacks or subplots. May include mental health themes, addiction, or loss.',
    examples: ['addiction', 'mental illness', 'parental neglect'],
    selectedBg:
      'bg-slate-500 text-white border-slate-500 dark:bg-slate-500 dark:text-white dark:border-slate-400',
  },
  {
    level: 3,
    name: 'Embrace the Abyss',
    emoji: '🌑',
    description:
      'Strong, present-day dark content with moderate graphic scenes. Deals with themes like abduction, trafficking, or revenge with emotional fallout.',
    examples: ['abduction', 'trafficking', 'revenge'],
    selectedBg:
      'bg-slate-700 text-white border-slate-700 dark:bg-slate-400 dark:text-slate-950 dark:border-slate-400',
  },
  {
    level: 4,
    name: 'Pitch Black',
    emoji: '🌑',
    description:
      'Heavy psychological or physical darkness, including captivity, dub-con, and deep manipulation. For readers who actively seek dark thrill elements.',
    examples: ['dub-con', 'captivity', 'manipulation'],
    selectedBg:
      'bg-slate-800 text-white border-slate-800 dark:bg-slate-300 dark:text-slate-950 dark:border-slate-300',
  },
  {
    level: 5,
    name: 'The Void',
    emoji: '🌑',
    description:
      'Extremely graphic, taboo, or triggering content. Includes non-con, torture, intense psychological abuse, and themes not suitable for all audiences.',
    examples: ['non-con', 'torture', 'taboo'],
    selectedBg:
      'bg-gray-950 text-white border-gray-950 dark:bg-white dark:text-gray-950 dark:border-white',
  },
] as const

// ═══════════════════════════════════════════════════
// SPICE LEVEL CARDS (matching SpiceDarkness.tsx)
// ═══════════════════════════════════════════════════
export const SPICE_LEVEL_CARDS = [
  {
    level: 1,
    name: 'Mild',
    emoji: '🌶️',
    description: 'Sweet kisses and cuddles. No on-page intimacy.',
    examples: ['Closed Door', 'Sweet Romance', 'Light Kissing'],
    selectedBg:
      'bg-amber-200 text-amber-900 border-amber-300 dark:bg-amber-700 dark:text-amber-100 dark:border-amber-600',
  },
  {
    level: 2,
    name: 'Warm',
    emoji: '🌶️',
    description: 'Flirting, some heat, and fade-to-black scenes.',
    examples: ['Open Door', 'Tension', 'Makeout Scenes'],
    selectedBg:
      'bg-orange-400 text-white border-orange-400 dark:bg-orange-500 dark:text-white dark:border-orange-400',
  },
  {
    level: 3,
    name: 'Spicy',
    emoji: '🌶️',
    description: 'On-page steamy scenes and descriptive romance.',
    examples: ['One Bed', 'High Heat', 'Dirty Talk'],
    selectedBg:
      'bg-red-500 text-white border-red-500 dark:bg-red-500 dark:text-white dark:border-red-400',
  },
  {
    level: 4,
    name: 'Extra Hot',
    emoji: '🌶️',
    description: 'Multiple explicit scenes with kink or taboo.',
    examples: ['BDSM', 'Praise/Degradation', 'Multiple Partners'],
    selectedBg:
      'bg-red-700 text-white border-red-700 dark:bg-red-400 dark:text-red-950 dark:border-red-400',
  },
  {
    level: 5,
    name: 'Inferno',
    emoji: '🌶️',
    description:
      'Highly explicit content with little to no plot focus. Intense, graphic, and unapologetically sexual.',
    examples: ['Erotica', 'Taboo', 'Explicit'],
    selectedBg:
      'bg-red-900 text-white border-red-900 dark:bg-red-300 dark:text-red-950 dark:border-red-300',
  },
] as const

// ═══════════════════════════════════════════════════
// BURN LEVEL CARDS (matching BurnLevels.tsx)
// ═══════════════════════════════════════════════════
export const BURN_LEVEL_CARDS = [
  {
    level: 1,
    name: 'Slow Burn',
    emoji: '🔥',
    description:
      'The romantic tension builds gradually over the entire story. Expect longing glances, near-misses, and delayed gratification that makes the payoff all the sweeter.',
    examples: ['Enemies to Lovers', 'Friends to Lovers', 'Pining'],
    selectedBg:
      'bg-blue-500 text-white border-blue-500 dark:bg-blue-400 dark:text-blue-950 dark:border-blue-400',
  },
  {
    level: 2,
    name: 'Medium Burn',
    emoji: '🔥',
    description:
      'A balanced pace — some early tension with a satisfying mid-story payoff. The romance develops naturally without dragging or rushing.',
    examples: ['Second Chance', 'Forced Proximity', 'Fake Dating'],
    selectedBg:
      'bg-amber-500 text-white border-amber-500 dark:bg-amber-400 dark:text-amber-950 dark:border-amber-400',
  },
  {
    level: 3,
    name: 'Fast Burn',
    emoji: '🔥',
    description:
      'The romance ignites quickly. Feelings develop early and intensely — perfect for readers who want the heat without the wait.',
    examples: ['Instalove', 'Insta-Lust', 'One Night Stand'],
    selectedBg:
      'bg-red-500 text-white border-red-500 dark:bg-red-400 dark:text-red-950 dark:border-red-400',
  },
] as const

// ═══════════════════════════════════════════════════
// CHARACTER ARCHETYPES BY GENRE
// ═══════════════════════════════════════════════════
export const CHARACTER_ARCHETYPES_BY_GENRE: Record<string, string[]> = {
  Romance: [
    'Alpha Male',
    'Cinnamon Roll',
    'Bad Boy',
    'Sunshine',
    'Grumpy',
    'Morally Grey',
    'Ice Queen',
    'Brooding Hero',
    'Protective Hero',
    'Damaged Hero',
    'Reformed Rake',
    'Reluctant Hero',
    'Villain Hero',
  ],
  Fantasy: [
    'Chosen One',
    'Mentor',
    'Reluctant Hero',
    'Dark Lord',
    'Trickster',
    'Warrior',
    'Healer',
    'Rogue',
    'Seer',
    'Shapeshifter',
  ],
  'Science Fiction': [
    'AI / Robot',
    'Space Captain',
    'Scientist',
    'Rebel Leader',
    'Clone',
    'Cyborg',
    'Time Traveler',
    'Alien Ambassador',
  ],
  Thriller: [
    'Detective',
    'Unreliable Narrator',
    'Femme Fatale',
    'Mastermind',
    'Vigilante',
    'Double Agent',
    'Profiler',
  ],
  Mystery: [
    'Detective',
    'Unreliable Narrator',
    'Femme Fatale',
    'Mastermind',
    'Amateur Sleuth',
    'Sidekick',
    'Red Herring Character',
  ],
  Horror: [
    'Final Girl',
    'Monster',
    'Survivor',
    'Investigator',
    'Possessed',
    'Skeptic',
    'Occultist',
  ],
  'Contemporary Fiction': [
    'Everyman',
    'Outcast',
    'Leader',
    'Caretaker',
    'Rebel',
    'Dreamer',
    'Perfectionist',
  ],
  'Literary Fiction': [
    'Everyman',
    'Outcast',
    'Leader',
    'Caretaker',
    'Rebel',
    'Dreamer',
    'Perfectionist',
  ],
  'General Fiction': [
    'Everyman',
    'Outcast',
    'Leader',
    'Caretaker',
    'Rebel',
    'Dreamer',
    'Perfectionist',
  ],
}

// Fallback archetypes for any genre not listed
export const DEFAULT_CHARACTER_ARCHETYPES = [
  'Everyman',
  'Outcast',
  'Leader',
  'Caretaker',
  'Rebel',
  'Dreamer',
  'Perfectionist',
  'Mentor',
  'Trickster',
  'Warrior',
]

// ═══════════════════════════════════════════════════
// REPRESENTATION TAGS (from Representation.tsx)
// ═══════════════════════════════════════════════════
export const REPRESENTATION_TAGS: Record<string, string[]> = {
  'Sexual Orientation & Gender': [
    'LGBTQ+',
    'Lesbian',
    'Gay',
    'Bisexual',
    'Transgender',
    'Non-binary',
    'Asexual',
    'Pansexual',
    'Intersex',
    'Queer',
  ],
  'Neurodiversity & Disability': [
    'Neurodivergent',
    'Autism',
    'ADHD',
    'Physical Disability',
    'Chronic Illness',
    'Deaf / Hard of Hearing',
    'Blind / Low Vision',
  ],
  'Mental Health': [
    'Mental Health',
    'Anxiety',
    'Depression',
    'PTSD',
    'OCD',
    'Eating Disorder',
    'Addiction Recovery',
  ],
  'Body Diversity': ['Body Positive', 'Plus Size', 'Scarred MC', 'Amputee MC'],
  'Race & Ethnicity': [
    'BIPOC',
    'Black',
    'Asian',
    'Hispanic / Latinx',
    'Indigenous',
    'Mixed Race',
    'Middle Eastern',
    'South Asian',
    'Pacific Islander',
  ],
  Age: [
    'Young Adult MC',
    'New Adult MC',
    'Mature MC',
    'Middle-Aged MC',
    'Elderly MC',
  ],
}

export const ALL_REPRESENTATION_TAGS = Object.values(REPRESENTATION_TAGS).flat()

// ═══════════════════════════════════════════════════
// SETTING TYPES
// ═══════════════════════════════════════════════════
export const SETTING_TYPES = [
  'Contemporary',
  'Historical',
  'Futuristic',
  'Fantasy World',
  'Small Town',
  'Big City',
  'Rural',
  'Suburban',
  'Academic',
  'Military',
  'Underwater',
  'Space',
  'Underground',
  'Island',
] as const

// ═══════════════════════════════════════════════════
// ATMOSPHERE / SCENE TAGS
// ═══════════════════════════════════════════════════
export const ATMOSPHERE_TAGS = [
  'Dark Forest',
  'Isolated Cabin',
  'Gothic Manor',
  'Haunted House',
  'Underground Tunnels',
  'Abandoned Building',
  'Snowy Mountains',
  'Desert Wasteland',
  'Tropical Island',
  'Underwater City',
  'Space Station',
  'Dystopian City',
  'Medieval Castle',
  'Victorian London',
  'Neon-Lit Streets',
  'Misty Moors',
  'Enchanted Garden',
  'War-Torn Landscape',
  'Cozy Bookshop',
  'Coffee Shop',
  'Beach Town',
  'Mountain Lodge',
  'Boarding School',
  'Prison',
  'Hospital',
  'Courtroom',
  'Ballroom',
  'Throne Room',
  'Arena',
  'Marketplace',
  'Pirate Ship',
  'Train',
  'Countryside Estate',
  'Rooftop',
  'Library',
  'Graveyard',
] as const

// ═══════════════════════════════════════════════════
// TIME PERIODS
// ═══════════════════════════════════════════════════
export const TIME_PERIODS = [
  'Ancient',
  'Medieval',
  'Renaissance',
  'Regency',
  'Victorian',
  '1920s',
  '1940s–50s',
  '1960s–70s',
  '1980s–90s',
  'Modern Day',
  'Near Future',
  'Far Future',
  'Timeless / Unspecified',
] as const
